"use client";

import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useSubscription, type UseSubscriptionResult } from "@/hooks/useSubscription";
import { AUTH_CHANGED_EVENT } from "@/lib/authEvents";

const SubscriptionContext = createContext<UseSubscriptionResult | null>(null);

/**
 * App-wide subscription state. Fetches GET /subscriptions/me ONCE (via
 * useSubscription) and shares the result with every consumer, instead of each
 * component firing its own request.
 *
 * Pro members get a gold app surface: when isPro, this provider overrides the
 * --color-surface / --color-on-surface CSS variables on a wrapper element, so
 * every `bg-surface` / `text-on-surface` element below it switches from green
 * to gold with dark text. --color-primary is deliberately NOT touched, so
 * buttons, badges, and tab icons stay green.
 *
 * The surface is a metallic gold gradient — champagne highlight → antique gold
 * → deep bronze — to read as premium/VIP rather than a flat orange. The diagonal
 * sheen is what signals "metal" (luxury) vs a paint swatch. --color-surface holds
 * the antique-gold mid-tone as a solid fallback (older engines / theme-color).
 * Text flips to near-black: #1A1A1A clears WCAG AA across the gradient (~10:1 on
 * the champagne highlight where the header text sits; ≥3.5:1 on the bronze end).
 */
const PRO_SURFACE_STYLE: CSSProperties = {
  "--color-surface": "#C9A24B",
  "--surface-gradient": "linear-gradient(135deg, #E5C97A 0%, #C9A24B 45%, #8A6A28 100%)",
  "--color-on-surface": "#1A1A1A",
  // Tints the gold/tan decorative artwork (antler/branch, ellipse) to brand
  // green on the gold surface — the inverse of the free tier (green surface,
  // gold artwork). Applied via .surface-decoration on the decorative images.
  "--surface-decoration-filter": "brightness(0.45) sepia(1) saturate(4) hue-rotate(58deg)",
} as CSSProperties;

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const subscription = useSubscription();
  const { refresh } = subscription;
  const pathname = usePathname();

  // Refetch on client-side navigation. This is what makes the tier resolve
  // right after login: the root provider never remounts, so without this it
  // would keep the pre-login (unauthenticated) snapshot. Skip the first run —
  // useSubscription already fetches once on mount.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    refresh();
  }, [pathname, refresh]);

  // Explicit auth-change signal (login/logout) + cross-tab token changes.
  useEffect(() => {
    const onAuthChanged = () => refresh();
    // Only react to the token key (or a full localStorage.clear(), key === null),
    // not unrelated same-origin writes like profile saves.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "token" || e.key === null) refresh();
    };
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  // Stable context value: usePathname re-renders this provider on every route
  // change, and `subscription` is a fresh object each render — memoize on the
  // actual fields so consumers only re-render when the tier really changes.
  const value = useMemo<UseSubscriptionResult>(
    () => ({
      tier: subscription.tier,
      isPro: subscription.isPro,
      isAuthenticated: subscription.isAuthenticated,
      loading: subscription.loading,
      refresh: subscription.refresh,
    }),
    [
      subscription.tier,
      subscription.isPro,
      subscription.isAuthenticated,
      subscription.loading,
      subscription.refresh,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {/* T-124: this used to be a display:contents carrier — no layout box, so
          the CSS-variable inline style was believed to add zero layout impact.
          On iOS standalone (installed PWA) WebKit drops a display:contents
          element's INHERITED custom properties for one frame during the
          route-transition reflow, so bg-surface / the active tab pill
          momentarily fell back to the green :root default on every tab switch
          — a gold→green→gold flash that only reproduced in standalone mode,
          never in the browser.
          Fix: a real block-level box (no className) holds its own inline
          style stably across the reflow — the div still generates a box, but
          an unstyled block (height:auto, width:auto, no background) has zero
          visual/layout footprint, so this is a safe drop-in.
          Deliberately NOT document.documentElement/<html> + useLayoutEffect:
          that global + imperative approach (PR #56) was reverted the same day
          with the regression still live. Staying scoped + declarative here
          means no imperative cleanup, no global mutation, and the provider
          still doesn't remount on client-side nav (router.push), so this
          carrier is what persists the tier's visual state across tab
          switches. */}
      <div style={subscription.isPro ? PRO_SURFACE_STYLE : undefined}>{children}</div>
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext(): UseSubscriptionResult {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscriptionContext must be used within a SubscriptionProvider");
  }
  return ctx;
}
